import { zoneListQuerySchema } from '@chukua/common';
import { useListQuery } from '../../../../../shared/hooks/use-list-query';
import { useColorMode } from '../../../../../shared/features/theme/components/ColorModeProvider';
import { useZoneKinds } from '../../../../reference-data/features/zone-kinds/public/use-zone-kinds';
import { useZoneNavigation } from '../../../hooks/use-zone-navigation';
import type { ZoneView } from '../../../zones.paths';
import { useZonesForMap, type ZonesForMapState } from './use-zones-for-map';
import { useZonesMap, type MapFrame } from './use-zones-map';
import { useZonesMapConfig } from '../../../public/use-zones-map-config';
import { kindCounts, zonesBounds } from '../policies/zones-map-data';

/** An open zone frames the map on itself (useZonePanel), so the results don't pull the view away from it. */
function frameFor(state: ZonesForMapState, openZoneId: string | undefined): MapFrame {
  if (state.status !== 'loaded') return { key: 'loading', bounds: null };
  return { key: state.queryKey, bounds: openZoneId ? null : zonesBounds(state.zones) };
}

/**
 * A zones map's state: the zones the search and filters match, the kinds they are coloured by, the map
 * itself, and the zone whose panel is open (`openZoneId`, or undefined). The map view uses it, and so does the
 * table's zone sheet (`view` 'list'), whose map shows the zones the table does.
 */
export function useZonesMapView(openZoneId: string | undefined, view: ZoneView = 'map') {
  const list = useListQuery(zoneListQuerySchema);
  const mapZones = useZonesForMap(list.query);
  const zoneKinds = useZoneKinds();
  const mapConfig = useZonesMapConfig();
  const tileKey = mapConfig?.tileKey;
  const navigation = useZoneNavigation(view);

  const zones = mapZones.state.status === 'loaded' ? mapZones.state.zones : [];
  const kinds = zoneKinds.state.status === 'loaded' ? zoneKinds.state.kinds : [];
  const frame = frameFor(mapZones.state, openZoneId);
  const { mode } = useColorMode();
  const { containerRef, map } = useZonesMap({
    tileKey: tileKey ?? null,
    mode,
    company: mapConfig?.company ?? null,
    zones,
    zoneKinds: kinds,
    editingZoneId: openZoneId ?? null,
    frame,
    onSelect: navigation.openZone,
  });

  return {
    list,
    mapZones,
    zoneKindsState: zoneKinds.state,
    kinds,
    legend: kindCounts(zones, kinds),
    tileKey,
    containerRef,
    map,
    navigation,
  };
}
